if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.index = 0;
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-bw-step]');
        const thumbnail = event.target.closest('[data-bw-media-id]');
        if (step) this.setActiveIndex(this.index + Number(step.dataset.bwStep));
        if (thumbnail) this.setActiveIndex(this.slides.findIndex(slide => slide.dataset.mediaId === thumbnail.dataset.bwMediaId));
      });
      this.addEventListener('keydown', this.onKeyDown.bind(this));
      this.stage = this.querySelector('.bw-media-stage');
      this.zoomButton = this.querySelector('[data-bw-zoom]');
      this.zoomButton?.addEventListener('click', () => this.setZoom(this.scale > 1 ? 1 : 2.5));
      this.scale = 1;
      this.pan = { x: 0, y: 0 };
      this.bindGestures();
    }
    show(opener) {
      // Variant changes replace the content: always collect fresh nodes.
      this.slides = [...this.querySelector('.product-media-modal__content').children];
      this.thumbnails = [...this.querySelectorAll('[data-bw-media-id]')];
      this.thumbnails.forEach(button => this.mountMedia(button));
      this.gallery = opener.closest('media-gallery');
      this.setActiveIndex(Math.max(0, this.slides.findIndex(slide => slide.dataset.mediaId === opener.dataset.mediaId)));
      super.show(opener);
      this.resizeController?.abort();
      this.resizeController = new AbortController();
      window.addEventListener('resize', () => this.applyTransform(), { signal: this.resizeController.signal });
      this.revealThumbnail();
    }
    hide() {
      const mediaId = this.slides?.[this.index]?.dataset.mediaId;
      if (mediaId && this.gallery) {
        const sectionId = this.gallery.id.replace('MediaGallery-', '');
        this.gallery.setActiveMedia(`${sectionId}-${mediaId}`, false);
        this.openedBy = this.gallery.querySelector(`button[data-media-id="${mediaId}"]`) || this.openedBy;
      }
      this.gesture = null;
      this.resizeController?.abort();
      this.setZoom(1);
      super.hide();
    }
    setActiveIndex(index) {
      if (!this.slides?.length) return;
      this.setZoom(1);
      pauseAllMedia();
      this.index = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.hidden = !active;
        slide.inert = !active;
        slide.classList.toggle('active', active);
        if (active) {
          this.mountMedia(slide);
          slide.querySelector('img')?.setAttribute('loading', 'eager');
        }
      });
      const current = this.slides[this.index];
      if (this.zoomButton) this.zoomButton.hidden = current.dataset.mediaType !== 'image';
      this.thumbnails.forEach(button => {
        const active = button.dataset.bwMediaId === current.dataset.mediaId;
        button.tabIndex = active ? 0 : -1;
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      this.querySelector('.bw-media-caption').textContent = current.dataset.caption || '';
      this.querySelector('.bw-media-counter').textContent = `${this.index + 1} / ${this.slides.length}`;
      this.querySelectorAll('[data-bw-step]').forEach(button => { button.hidden = this.slides.length < 2; });
      if (this.hasAttribute('open')) this.revealThumbnail();
    }
    get activeImage() { return this.slides?.[this.index]?.querySelector('img'); }
    setZoom(scale) {
      const image = this.activeImage;
      if (image) image.style.transform = '';
      this.scale = image && this.slides[this.index].dataset.mediaType === 'image' ? scale : 1;
      this.pan = { x: 0, y: 0 };
      this.stage.classList.toggle('is-zoomed', this.scale > 1);
      if (this.zoomButton) {
        this.zoomButton.setAttribute('aria-pressed', String(this.scale > 1));
        this.zoomButton.textContent = this.scale > 1 ? 'Verkleinern −' : 'Vergrößern +';
      }
      this.applyTransform();
    }
    applyTransform() {
      const image = this.activeImage;
      const slide = this.slides?.[this.index];
      if (!image || !slide || !this.hasAttribute('open')) return;
      const ratio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 1;
      const width = Math.min(image.clientWidth, image.clientHeight * ratio);
      const height = Math.min(image.clientHeight, image.clientWidth / ratio);
      const maxX = Math.max(0, (width * this.scale - slide.clientWidth) / 2);
      const maxY = Math.max(0, (height * this.scale - slide.clientHeight) / 2);
      this.pan.x = Math.max(-maxX, Math.min(maxX, this.pan.x));
      this.pan.y = Math.max(-maxY, Math.min(maxY, this.pan.y));
      image.style.transform = this.scale > 1 ? `translate3d(${this.pan.x}px, ${this.pan.y}px, 0) scale(${this.scale})` : '';
    }
    bindGestures() {
      this.stage.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) { this.gesture = null; return; }
        if (event.button !== 0 || event.target.closest('button, deferred-media, product-model') || this.slides?.[this.index]?.dataset.mediaType !== 'image') return;
        this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, time: event.timeStamp, panX: this.pan.x, panY: this.pan.y, dragged: false, zoomed: this.scale > 1 };
        if (this.scale > 1) this.stage.setPointerCapture(event.pointerId);
      });
      this.stage.addEventListener('pointermove', (event) => {
        const start = this.gesture;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.hypot(dx, dy) > 8) start.dragged = true;
        if (start.zoomed) {
          this.pan = { x: start.panX + dx, y: start.panY + dy };
          this.applyTransform();
          event.preventDefault();
        } else if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this.stage.setPointerCapture(event.pointerId);
          event.preventDefault();
        }
        if (start.dragged) this.suppressClickUntil = performance.now() + 500;
      });
      this.stage.addEventListener('pointerup', (event) => {
        const start = this.gesture;
        this.gesture = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (start.dragged) this.suppressClickUntil = performance.now() + 500;
        if (!start.zoomed && Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.2 && event.timeStamp - start.time < 1500) {
          this.suppressClickUntil = performance.now() + 500;
          this.setActiveIndex(this.index + (dx < 0 ? 1 : -1));
        }
      });
      const cancel = () => { this.gesture = null; };
      this.stage.addEventListener('pointercancel', cancel);
      this.stage.addEventListener('lostpointercapture', (event) => { if (event.target === this.stage) cancel(); });
      this.stage.addEventListener('click', (event) => {
        if (event.target.closest('button, deferred-media, product-model')) return;
        if (performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); return; }
        if (event.target.closest('.bw-media-slide') && this.slides?.[this.index]?.dataset.mediaType === 'image') this.setZoom(this.scale > 1 ? 1 : 2.5);
      });
      this.stage.addEventListener('load', () => this.applyTransform(), true);
      this.stage.addEventListener('dragstart', (event) => event.preventDefault());
    }
    mountMedia(container) {
      const template = container.querySelector('template[data-bw-media-template]');
      if (!template) return;
      template.replaceWith(template.content.cloneNode(true));
    }
    revealThumbnail() {
      const button = this.thumbnails.find(item => item.getAttribute('aria-current') === 'true');
      const strip = this.querySelector('.bw-media-thumbnails');
      if (button && strip) strip.scrollTo({ left: button.offsetLeft - strip.offsetLeft - (strip.clientWidth - button.clientWidth) / 2, behavior: 'instant' });
    }
    onKeyDown(event) {
      if (!this.hasAttribute('open')) return;
      if (event.key === 'Tab') {
        // Dawn's generic trap includes hidden media controls. Filter those out.
        const focusable = getFocusableElements(this).filter(item => item.tabIndex >= 0 && !item.closest('[hidden], [inert]') && item.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (!first) return;
        const outside = !focusable.includes(document.activeElement);
        if (event.shiftKey && (document.activeElement === first || outside)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || outside)) { event.preventDefault(); first.focus(); }
        event.stopPropagation();
        return;
      }
      if (event.target.closest('input, textarea, select, video, iframe, product-model')) return;
      if (this.scale > 1 && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        this.pan.x += event.key === 'ArrowLeft' ? 40 : event.key === 'ArrowRight' ? -40 : 0;
        this.pan.y += event.key === 'ArrowUp' ? 40 : event.key === 'ArrowDown' ? -40 : 0;
        this.applyTransform();
        return;
      }
      let next;
      if (event.key === 'ArrowLeft') next = this.index - 1;
      if (event.key === 'ArrowRight') next = this.index + 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.slides.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      const wasThumbnail = event.target.closest('[data-bw-media-id]');
      this.setActiveIndex(next);
      if (wasThumbnail) this.thumbnails.find(button => button.tabIndex === 0)?.focus({ preventScroll: true });
    }
  });
}
